import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import ReCAPTCHA from "react-google-recaptcha";

const recaptchaSiteKey = import.meta.env.VITE_GOOGLE_CAPTCHA_KEY;

// API URL setup
let apiUrl = "https://api.gimmefy.ai/v2";
const baseUrl = window.location.origin;
if (baseUrl.includes("localhost") || baseUrl.includes("127.0.0.1") || baseUrl.includes("dev.gimmefy.ai")) {
  apiUrl = "https://apidev.gimmefy.ai/v2";
} else if (baseUrl.includes("staging.gimmefy.ai")) {
  apiUrl = "https://apistaging.gimmefy.ai/v2";
}
interface EventRequestFormData {
  fullName: string;
  companyJobTitle: string;
  email: string;
  linkedinProfile?: string;
  keepMeUpdated: boolean;
}
interface EventRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventDay: string;
  eventDate: string;
  eventTime: string;
  eventLocation: string;
  eventVenue: string;
}
export const EventRequestDialog = ({
  open,
  onOpenChange,
  eventDay,
  eventDate,
  eventTime,
  eventLocation,
  eventVenue
}: EventRequestDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    reset
  } = useForm<EventRequestFormData>();

  // Handle reCAPTCHA change
  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };
  const onSubmit = async (data: EventRequestFormData) => {
    // Check if reCAPTCHA is completed
    if (!captchaValue) {
      toast.error("Please complete the reCAPTCHA verification");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        event_name: "PROMPT_AND_PLATE_SEAT_REQUEST",
        payload: {
          full_name: data.fullName,
          company_job_title: data.companyJobTitle,
          email: data.email,
          linkedin_profile: data.linkedinProfile || null,
          keep_me_updated: data.keepMeUpdated,
          event_day: eventDay,
          event_date: eventDate,
          event_time: eventTime,
          event_location: eventLocation,
          event_venue: eventVenue,
          timestamp: new Date().toLocaleString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            timeZoneName: 'short'
          })
        },
        captcha_code: captchaValue
      };

      const response = await fetch(`${apiUrl}/triggerNotificationPublic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to send notification');
      }

      toast.success("Thank you for your interest! We've received your seat request and will confirm your spot within 72 hours.");
      reset();
      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      onOpenChange(false);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent noScroll className="sm:max-w-[500px] max-h-[90vh] p-0" style={{ pointerEvents: 'auto' }}>
        <div className="p-6 pb-8" style={{ pointerEvents: 'auto' }}>
          <DialogHeader>
            <DialogTitle className="text-2xl">Request a seat</DialogTitle>
            <DialogDescription className="space-y-1">
              <p className="font-semibold text-foreground">
                {eventDay}, {eventDate}
              </p>
              <p className="text-sm">
                {eventTime} • {eventLocation} • {eventVenue}
              </p>
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 mt-4">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="fullName">
              Full name <span className="text-destructive">*</span>
            </Label>
            <Input id="fullName" {...register("fullName", {
            required: "Full name is required",
            maxLength: {
              value: 100,
              message: "Name must be less than 100 characters"
            }
          })} placeholder="Your full name" />
            {errors.fullName && <p className="text-sm text-destructive">{errors.fullName.message}</p>}
          </div>

          {/* Company and Job Title */}
          <div className="space-y-2">
            <Label htmlFor="companyJobTitle">
              Company and job title <span className="text-destructive">*</span>
            </Label>
            <Input id="companyJobTitle" {...register("companyJobTitle", {
            required: "Company and job title are required",
            maxLength: {
              value: 150,
              message: "Company and job title must be less than 150 characters"
            }
          })} placeholder="e.g., Marketing Director at Acme Corp" />
            {errors.companyJobTitle && <p className="text-sm text-destructive">{errors.companyJobTitle.message}</p>}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email address <span className="text-destructive">*</span>
            </Label>
            <Input id="email" type="email" {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address"
            },
            maxLength: {
              value: 255,
              message: "Email must be less than 255 characters"
            }
          })} placeholder="your.email@company.com" />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          {/* LinkedIn Profile */}
          <div className="space-y-2">
            <Label htmlFor="linkedinProfile">
              LinkedIn profile{" "}
              <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input id="linkedinProfile" type="url" {...register("linkedinProfile", {
            pattern: {
              value: /^https?:\/\/(www\.)?linkedin\.com\/.*$/,
              message: "Please enter a valid LinkedIn URL"
            }
          })} placeholder="https://linkedin.com/in/yourprofile" />
            {errors.linkedinProfile && <p className="text-sm text-destructive">{errors.linkedinProfile.message}</p>}
          </div>

          {/* Keep Me Updated Checkbox */}
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox id="keepMeUpdated" {...register("keepMeUpdated")} />
            <Label htmlFor="keepMeUpdated" className="text-sm font-normal cursor-pointer">
              Keep me updated on upcoming events
            </Label>
          </div>

          {/* reCAPTCHA */}
          <div className="space-y-2 pt-2" style={{ position: 'relative', zIndex: 100000001, pointerEvents: 'auto', isolation: 'isolate' }}>
            <div style={{ position: 'relative', zIndex: 100000002 }}>
              <ReCAPTCHA
                id="event-request-recaptcha"
                ref={recaptchaRef}
                sitekey={recaptchaSiteKey}
                onChange={handleCaptchaChange}
                theme="light"
              />
            </div>
            {!captchaValue && (
              <p className="text-sm text-muted-foreground mt-2">
                Please complete the reCAPTCHA verification above
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="space-y-4 pt-2">
            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting || !captchaValue}>
              {isSubmitting ? "Submitting..." : "Submit request"}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              We will review your application and respond within 72 hours. Submitting this form does not guarantee a seat.
            </p>
          </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>;
};